import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, AbstractControl,
  Validators, ValidatorFn, ValidationErrors
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuillModule, QuillModules } from 'ngx-quill';
import { ChipsComponent } from 'tmna-mqcs-ui/src/lib/components/chips';
import { Tooltip } from 'tmna-mqcs-ui/src/lib/components/tooltip';

import { MfgappsApiService } from 'src/app/services/mfgapps-api.service';
import { MfgappsEventService } from 'src/app/services/mfgapps-event.service';
import { CostCalculatorComponent } from '../cost-calculator/cost-calculator.component';
import { OperationalFocusCategoriesList, OperationalFocusCategoryItem } from 'src/app/models/operationalFocusCategoriesModel';
import { EntrySubmissionModel } from 'src/app/models/successEntryModel';
import { UserModel } from 'src/app/models/userModel';
import { Constants } from 'src/app/models/constants';
import { UserAutocompleteComponent } from "src/app/templates/user-autocomplete/user-autocomplete.component";
import { UploadDocumentsComponent } from '../upload-documents/upload-documents.component';

@Component({
  selector: 'app-submission',
  imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatIcon, MatTooltipModule,
    MatInputModule, MatSelectModule, MatCheckboxModule, MatDatepickerModule, MatNativeDateModule,
    MatButtonModule, MatCardModule, MatSlideToggleModule, MatProgressSpinnerModule,
    ChipsComponent, Tooltip, UserAutocompleteComponent, QuillModule],
  templateUrl: './submission.component.html',
  styleUrls: ['./submission.component.scss']
})
export class SubmissionComponent {
  private readonly destroyRef = inject(DestroyRef);
  currentUser: UserModel;
  isEditMode = false;
  userAccessLevel = 'Creator'; //Default to Creator; will be updated based on access level on entry edit
  contributorsList: { displayName: string, fullName: string, email: string }[] = [];
  readonly maxContributorsAllowed = 5;
  showContributorInput = false;
  entryDetails: EntrySubmissionModel;
  entryId: string;
  submissionPayload: EntrySubmissionModel;
  submissionForm: FormGroup;
  formSubmitted = false;
  costCalculationSteps: any[];
  previousShopCalculationSteps: any[]; // To restore previous calculation steps if needed in future
  companyList: any[];
  currentCompany: any;
  currentPlant: any;
  private _currentShop: any;
  isVehicleAvoidanceAllowed = false;
  todayDate = new Date();
  loader = { isLoading: true, message: 'Loading...' };
  errorMessage = '';
  checkLearningCategoryIsExclusive = true;
  operationalFocusCategories = structuredClone(OperationalFocusCategoriesList);
  //MRH-8908 Show only bold, italic, underline, bullet and numbered list options in quill editor toolbar
  quillModules: QuillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ]
  };

  constructor(private readonly formBuilder: FormBuilder, private readonly dialog: MatDialog,
    private readonly mfgappsApiService: MfgappsApiService, public readonly eventService: MfgappsEventService,
    private readonly router: Router, private readonly route: ActivatedRoute, private readonly location: Location) { }

  ngOnInit() {
    this.initializeComponent();
  }

  async initializeComponent() {
    try {
      this.loader.message = 'Loading Corporate Hierarchy...';
      this.companyList = await this.eventService.getCorporateHierarchy();
      this.initializeFormValidators();
      this.currentUser = this.eventService.getActiveAccount();
      this.entryId = this.route.snapshot.paramMap.get('entry_id');
      if (this.entryId) {
        await this.fetchEntryDetails(this.entryId);
      }
      else { 
        //Set current user's name as TM name in new entry mode
        this.submissionForm.get("tm_name").setValue(this.currentUser?.displayName);
      }
      this.resetLoader();
    }
    catch (error) {
      console.info(error);
      this.resetLoader();
      this.errorMessage = "Error occurred while Loading Corporate Hierarchy";
      this.eventService.showSnackBar(this.errorMessage, 'error');
    }
  }

  initializeFormValidators() {
    this.submissionForm = this.formBuilder.group({
      tm_name: [{ value: '', disabled: true }, [Validators.required, this.eventService.trimRequiredValidator()]],
      date: [this.todayDate, [Validators.required, this.maxDateValidator(this.todayDate)]],
      title: [' ', [Validators.required, this.eventService.trimRequiredValidator(), Validators.maxLength(100)]],
      company_id: ['', Validators.required],
      plant_id: ['', Validators.required],
      shop_id: ['', Validators.required],
      issues: ['', [Validators.required, this.eventService.trimRequiredValidator()]],
      actions: ['', [Validators.required, this.eventService.trimRequiredValidator()]],
      outcomes: ['', [Validators.required, this.eventService.trimRequiredValidator()]],
      wo_link: [''],
      edc_link: [''],
      asset_id: ['', [Validators.maxLength(20)]],
    });
  }

  get form() {
    return this.submissionForm.controls;
  }

  maxDateValidator(maxDate: Date): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const selectedDate = new Date(control.value);
      // Clearing Time fields from date value
      const selected = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      return selected <= max ? null : { maxDate: true };
    };
  }

  async fetchEntryDetails(entryId: any) {
    try {
      this.isEditMode = true;
      this.loader.message = 'Fetching Entry Details...';
      this.entryDetails = (await this.mfgappsApiService.getEntryDetails(entryId)).result;
      this.entryDetails.issues = this.entryDetails.issues.replaceAll('\r', '').replaceAll('\n', '<p></p>');
      this.submissionForm.patchValue({
        tm_name: this.entryDetails.tm_name,
        title: this.entryDetails.title,
        //date: new Date(this.entryDetails.date), //Date parsed from date only string is in UTC
        date: new Date(`${this.entryDetails.date}T00:00:00`),
        issues: this.entryDetails.issues,
        actions: this.entryDetails.actions,
        outcomes: this.entryDetails.outcomes,
        company_id: this.entryDetails.company_id,
        plant_id: this.entryDetails.plant_id,
        shop_id: this.entryDetails.shop_id,
        wo_link: this.entryDetails.maximo_workorder_link ?? '',
        edc_link: this.entryDetails.edc_link ?? '',
        asset_id: this.entryDetails.asset_id ?? '',
      });
      this.currentCompany = this.companyList.find(company => company.company_id === this.entryDetails.company_id);
      this.currentPlant = this.currentCompany?.plantList.find(plant => plant.plant_id === this.entryDetails.plant_id);
      this.currentShop = this.currentPlant?.shopList.find(shop => shop.shop_id === this.entryDetails.shop_id);
      this.contributorsList = this.eventService.parseContributorsList(this.entryDetails) ?? [];
      this.userAccessLevel = this.eventService.checkUserAccessToEntry(this.entryDetails);
      if (this.userAccessLevel === 'ShopAdmin') {
        this.submissionForm.get('shop_id').disable();
        this.submissionForm.get('plant_id').disable();
      }
      if (this.userAccessLevel === 'ShopAdmin' || this.userAccessLevel === 'CompanyAdmin') {
        this.submissionForm.get('company_id').disable();
      }
      this.costCalculationSteps = structuredClone(this.entryDetails.calc_steps);
      for (const category of this.operationalFocusCategories) {
        if (this.entryDetails[category.name]) {
          this.onOperationalFocusCategorySelect(category);
        }
      }
    }
    catch (error) {
      console.info(error);
      this.errorMessage = 'Error occurred while Fetching Entry Details, Please try again !';
      this.eventService.showSnackBar(this.errorMessage, 'error');
    }
  }

  addContributor(event: any) {
    let isAdded = false;
    const createdBy = this.isEditMode ? this.entryDetails.created_by : this.currentUser.emailAddress;
    if (this.contributorsList.length >= this.maxContributorsAllowed) {
      this.eventService.showSnackBar(`Maximum ${this.maxContributorsAllowed} Contributors allowed !`, 'warn');
    }
    else if (event.selectedUser.userPrincipalName === createdBy) {
      this.eventService.showSnackBar(`Entry Creator cannot be added as a Contributor !`, 'warn');
    }
    else if (this.contributorsList.find(user => user.email === event.selectedUser.userPrincipalName)) {
      this.eventService.showSnackBar(`Contributor '${event.selectedUser.displayName}' already exists !`, 'warn');
    }
    else {
      const bracketIndex = event.selectedUser.displayName.lastIndexOf('(');
      const slicedName = event.selectedUser.displayName.slice(0, bracketIndex).trim();
      //Push to contributors list with display name and email, remove suffix e.g. (TMNA) inside parentheses
      this.contributorsList.push({
        fullName: slicedName,
        displayName: this.eventService.formatContributorName(slicedName),
        email: event.selectedUser.userPrincipalName.trim()
      });
      isAdded = true;
    }
    return isAdded;
  }

  openContributorInput() {
    if (this.contributorsList.length >= this.maxContributorsAllowed) {
      this.eventService.showSnackBar(`Maximum ${this.maxContributorsAllowed} Contributors allowed !`, 'warn');
      return;
    }
    this.showContributorInput = true;
  }

  closeContributorInput() {
    this.showContributorInput = false;
  }

  onContributorSelected(event: any) {
    if (this.addContributor(event)) {
      this.showContributorInput = false;
    }
  }

  removeContributor(contributor: any) {
    this.contributorsList = this.contributorsList.filter(user => user.email !== contributor.email);
  }

  async onCompanyChange(companyId: number) {
    if (!await this.showCalculatorResetWarning('Company')) {
      this.submissionForm.get('company_id').setValue(this.currentCompany?.company_id);
      return;
    }
    this.currentCompany = this.companyList.find(company => company.company_id === companyId);
    this.submissionForm.get('shop_id').reset();
    this.submissionForm.get('plant_id').reset();
  }

  async onPlantChange(plantId: number) {
    if (!await this.showCalculatorResetWarning('Plant')) {
      this.submissionForm.get('plant_id').setValue(this.currentPlant?.plant_id);
      return;
    }
    this.currentPlant = this.currentCompany?.plantList.find(plant => plant.plant_id === plantId);
    this.submissionForm.get('shop_id').reset();
  }

  async onShopChange(shopId: number) {
    if (!await this.showCalculatorResetWarning('Shop')) {
      this.submissionForm.get('shop_id').setValue(this._currentShop?.shop_id);
      return;
    }
    this.currentShop = this.currentPlant?.shopList.find(shop => shop.shop_id === shopId);
  }
  
  set currentShop(shop: any) {
    this._currentShop = shop;
    this.isVehicleAvoidanceAllowed = this.eventService.checkIfShopHasVehicleAvoidance(this._currentShop);
  }

  async showCalculatorResetWarning(dropdownType: string) {
    //Show confirmation dialog on company/plant/shop change by user when cost calculation steps already exist
    if (!this.costCalculationSteps || !this.submissionForm.get('productivityDetails') 
      || await this.eventService.confirmDialog(
      `Changing the ${dropdownType} will reset the existing Cost Avoidance calculations. Do you wish to proceed?`)) {
      this.previousShopCalculationSteps = this.costCalculationSteps;
      this.costCalculationSteps = null;
      this.submissionForm.get('productivityDetails')?.patchValue({ costAvoidance: 0, numberOfVehicles: 0 });
      return true;
    }
    return false;
  }

  async onOperationalFocusCategorySelect(category: OperationalFocusCategoryItem) {
    //Show warning only if some category is unselected by user and some data is present for the category
    if (category.selected) {
      const formValues = this.submissionForm.getRawValue();
      const hasData = (category.name !== 'productivity' && formValues[category.fieldName].trim() !== '')
        || (category.name === 'productivity' && this.checkIfProductivityDataPresent());
      if (hasData && !await this.eventService.confirmDialog(
        'Existing operational focus category data will be deleted if unselected. Do you wish to proceed?')) {
        return;
      }
    }
    if (!this.checkLearningCategoryIsExclusive || this.checkOperationalFocusLogic(category)) {
      category.selected = !category.selected;
      this.setEachOperationalFocusDisabled();
      this.updateOperationalCategoryFields(category);
    }
  }

  checkOperationalFocusLogic(category: OperationalFocusCategoryItem) {
    //Always allow to process if any category is unselected, check other conditions only on selection
    return (category.selected || (category.displayName === 'Learning' && !this.otherThanLearningSelected)
      || (category.displayName !== 'Learning' && !this.learningCategory.selected));
  }

  setEachOperationalFocusDisabled() {
    //Set disabled status for each operational focus based on learning category exclusivity flag
    if (this.checkLearningCategoryIsExclusive) {
      for (const each of this.operationalFocusCategories) {
        each.disabled = (each.displayName !== this.learningCategory.displayName && this.learningCategory.selected)
          || (each.displayName === this.learningCategory.displayName && this.otherThanLearningSelected);
      }
    }
  }

  get otherThanLearningSelected() {
    return this.operationalFocusCategories.some(each => each.displayName !== 'Learning' && each.selected);
  }

  get learningCategory() {
    return this.operationalFocusCategories.find(each => each.displayName === "Learning");
  }

  updateOperationalCategoryFields(category: OperationalFocusCategoryItem) {
    if (category.displayName === 'Productivity') {
      if (category.selected) {
        this.submissionForm.addControl('productivityType', 
          this.formBuilder.control(this.entryDetails?.data_type ?? false, Validators.required));
        this.onProductivityToggleChange(false);
      }
      else {
        this.submissionForm.removeControl('productivityType');
        this.submissionForm.removeControl('productivityDetails');
      }
    }
    else if (category.selected) {
      //For non-productivity categories, add or remove corresponding fields based on category selection
      this.submissionForm.addControl(category.fieldName,
        this.formBuilder.control(this.entryDetails?.[category.fieldName] ?? ' ',
          [Validators.required, this.eventService.trimRequiredValidator(), Validators.maxLength(500)]));
    }
    else {
      this.submissionForm.removeControl(category.fieldName);
    }
  }

  checkIfProductivityDataPresent() {
    const productivityDetails = this.submissionForm.getRawValue().productivityDetails || {};
    return productivityDetails.costAvoidance > 0 || productivityDetails.numberOfVehicles > 0
      || this.costCalculationSteps?.length > 0 || productivityDetails.minutesSaved > 0
      || (productivityDetails.event && productivityDetails.event.trim() !== '');
  }

  get productivityControls() {
    return (this.submissionForm.get('productivityDetails') as FormGroup).controls;
  }

  async onProductivityToggleChange(isUserTriggered = true) {
    const isEventTypeProductivity = this.submissionForm.get('productivityType').value;
    //Ask confirmation only when cost avoidance/minutes saved > 0 or event description is entered
    if (isUserTriggered && this.checkIfProductivityDataPresent()) {
      const confirmMessage = (isEventTypeProductivity ? 'You may lose all downtime cost calculation data.' 
      : 'You may lose all Productivity event related data.') + ' Do you wish to proceed?';
      if (!await this.eventService.confirmDialog(confirmMessage)) {
        //Revert the toggle change and return if user selects No
        this.submissionForm.get('productivityType').setValue(!isEventTypeProductivity);
        return;
      }
    }
    if (isEventTypeProductivity) {
      this.previousShopCalculationSteps = this.costCalculationSteps;
      this.costCalculationSteps = null;
      this.submissionForm.setControl('productivityDetails', this.formBuilder.group({
        minutesSaved: [this.entryDetails?.num_minutes ?? 0, [Validators.min(0.01)]],
        event: [this.entryDetails?.event ?? ' ', [Validators.required, this.eventService.trimRequiredValidator(), Validators.maxLength(500)]]
      }));
    }
    else {
      this.submissionForm.setControl('productivityDetails', this.formBuilder.group({
        costAvoidance: [{ value: this.entryDetails?.cost_avoidance ?? 0, disabled: true }],
        numberOfVehicles: [{ value: this.entryDetails?.num_vehicles ?? 0, disabled: true }],
      }));
    }
  }

  removeHtmlSpaceTags(text: string) {
    //trim and remove only one &nbsp; occurrence and retain other &nbsp; to preserve additional spaces in HTML formatting
    return text.trim().replace(/&nbsp;(?!&nbsp;)/g, ' ');
  }

  async onSubmit() {
    if (!this.operationalFocusCategories.some(category => category.selected)) {
      this.eventService.showSnackBar('Atleast one Operational Focus should be selected !!', 'warn');
      return;
    }
    this.formSubmitted = true;
    this.submissionForm.markAllAsTouched();
    if (!this.submissionForm.valid) {
      this.eventService.showSnackBar('Please Clear Validation Errors before submitting entry', 'error');
      return;
    }
    const formValues = this.submissionForm.getRawValue();
    this.submissionPayload = {
      tm_name: formValues.tm_name.trim(),
      tm_number: this.isEditMode ? this.entryDetails.tm_number : this.currentUser.workdayID,
      title: formValues.title.trim(),
      contributors_email_id: this.contributorsList.map(contributor => contributor.email).join(','),
      contributors_name: this.contributorsList.map(contributor => contributor.fullName).join(','),
      //date": formValues.date.toISOString().slice(0, 10), //saves next date if saved after 7 PM CST
      date: this.eventService.getISOStringLocalDate(formValues.date),
      corporate_id: this.eventService.corporateId,
      company_id: formValues.company_id,
      plant_id: formValues.plant_id,
      shop_id: formValues.shop_id,
      issues: this.removeHtmlSpaceTags(formValues.issues),
      actions: this.removeHtmlSpaceTags(formValues.actions),
      outcomes: this.removeHtmlSpaceTags(formValues.outcomes),
      maximo_workorder_link: formValues.wo_link.trim(),
      edc_link: formValues.edc_link.trim(),
      asset_id: formValues.asset_id.trim(),
      calc_steps: this.costCalculationSteps,
      core_function_id: this.eventService.coreFunctionId,
      subcore_function_id: this.eventService.subcoreFunctionId
    };
    for (const category of this.operationalFocusCategories) {
      this.submissionPayload[category.name] = category.selected;
      if (category.displayName === 'Productivity' && category.selected) {
        this.submissionPayload.data_type = formValues.productivityType;
        if (this.submissionPayload.data_type) {
          this.submissionPayload.num_minutes = formValues.productivityDetails.minutesSaved;
          this.submissionPayload.event = formValues.productivityDetails.event.trim();
        }
        else {
          this.submissionPayload.cost_avoidance = formValues.productivityDetails.costAvoidance;
          this.submissionPayload.num_vehicles = formValues.productivityDetails.numberOfVehicles;
        }
      }
      else if (category.selected) {
        this.submissionPayload[category.fieldName] = formValues[category.fieldName].trim();
      }
    }
    //If productivity health type is selected, cost avoidance calculation steps should be available
    if (this.submissionPayload.productivity && !this.submissionPayload.data_type
      && (!this.costCalculationSteps || this.costCalculationSteps.length === 0)) {
      this.eventService.confirmDialog('Cost Avoidance is not calculated. Please click on calculator icon to calculate.', true);
      return;
    }
    //API call to entry New submission or Update existing entry
    try {
      const showConfirmationMsg = false;
      const confirmationMessage = !this.isEditMode ? 'Do you want to submit the new entry ?'
        : 'Updating the entry will replace existing data with current form inputs. Do you wish to proceed?';
      if (showConfirmationMsg && !await this.eventService.confirmDialog(confirmationMessage)) {
        return;
      }
      this.loader.message = `${this.isEditMode ? 'Updating' : 'Submitting New'} Entry...`;
      this.loader.isLoading = true;
      const response = this.isEditMode ? 
        await this.mfgappsApiService.updateEntry(this.entryId, this.submissionPayload) :
        await this.mfgappsApiService.submitEntry(this.submissionPayload);
      this.resetLoader();
      const resultMessage = `Entry '${this.submissionPayload.title}' ${this.isEditMode ? 'Updated' : 'Created'} Successfully !`;
      this.eventService.showSnackBar(resultMessage, 'success');
      if (!this.isEditMode) {
        //Store the entryId from post response and just update the URL, page won't reload
        this.entryId = response.result.entry_id;
        this.location.replaceState(`${Constants.EntryRoute}/${this.entryId}/edit`);
      }
      this.submissionForm.markAsPristine();
      const documentConfirmationMessage = `${resultMessage},
      Would you like to upload supporting images and documents for this entry?`;
      if (!this.isEditMode && await this.eventService.confirmDialog(documentConfirmationMessage)) {
        this.uploadDocuments();
      }
      else {
        setTimeout(() => {
          this.router.navigate([`${Constants.EntryRoute}/${this.entryId}/view`]);
        }, 500);
      }
    }
    catch (error) {
      console.info(error);
      const errorMessage = `Error occurred while ${this.loader.message}, Please try again !`;
      this.resetLoader();
      this.eventService.showSnackBar(errorMessage, 'error');
    }
  }

  uploadDocuments() {
    const uploadDialog = this.dialog.open(UploadDocumentsComponent, {
      disableClose: true,
      maxWidth: '98vw',
      maxHeight: '98vh',
      height: '98%',
      width: '98%',
      data: {
        title: 'Upload Supporting Images and Documents',
        entryId: this.entryId,
        isEditMode : this.isEditMode
      }
    });
    if (!this.isEditMode) {
      uploadDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_response => {
        this.router.navigate([`${Constants.EntryRoute}/${this.entryId}/view`]);
      });
    }
  }

  async onBack(backToView = false) {
    //show confirmation dialog to user only if there are unsaved changes, if pristine then navigate without asking
    if (this.submissionForm.pristine ||     (this.submissionForm.dirty 
      && await this.eventService.confirmDialog( 'Any unsaved data will be lost if you go back. Do you wish to proceed?'))) {
      const navigationOptions = { queryParams: { entryId: this.entryId } };
      if (this.isEditMode && backToView) {
        this.router.navigate([`${Constants.EntryRoute}/${this.entryId}/view`]);
      }
      else {
        this.router.navigate([Constants.EntriesRoute], this.isEditMode ? navigationOptions : {});
      }
    }
  }

  resetLoader() {
    this.loader.isLoading = false;
    this.loader.message = '';
  }

  openCalculator() {
    //Don't show calculator if event type productivity is selected
    if (this.submissionForm.get('productivityType').value) {
      return;
    }
    const cpsFields = ['company_id', 'plant_id', 'shop_id'].map(field => this.submissionForm.get(field));
    cpsFields.forEach(field => field.markAsTouched());
    this.formSubmitted = true;
    if (cpsFields.some(field => field.invalid)) {
      this.eventService.showSnackBar('Please select Company, Plant and Shop', 'warn');
      return;
    }
    cpsFields.forEach(field => field.markAsUntouched());
    const calculatorDialog = this.dialog.open(CostCalculatorComponent, {
      disableClose: true,
      maxWidth: '98vw',
      maxHeight: '98vh',
      height: '98%',
      width: '98%',
      data: {
        title: 'Downtime Cost Calculator',
        entryDetails: this.submissionForm.getRawValue(),
        costCalculationSteps: this.costCalculationSteps
      }
    });
    calculatorDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      if (response) {
        //MRH-8261 After successful calculation, update the cost avoidance & number of vehicles fields in parent entry
        this.submissionForm.get('productivityDetails')?.patchValue({
          costAvoidance: response.totalCostAvoidance,
          numberOfVehicles: response.totalVehicleAvoidance
        });
        this.submissionForm.markAsDirty();
        this.costCalculationSteps = response.calculationSteps;
      }
    });
  }
}